import { useEffect, useMemo, useState } from 'react'
import { ApiError, api } from '../lib/api'
import { userMessageForError } from '../lib/userError'
import type {
  AdminDashboard,
  AdminEvidenceView,
  AdminMe,
  AdminOperationAuditPage,
  AdminPlanCode,
  AdminReport,
  AdminSong,
  AdminSongControlAction,
  AdminSongPage,
  AdminSongReportFilter,
  AdminSongSort,
  AdminSongSourceFilter,
  AdminSongStatusFilter,
  AdminUser,
  AdminUserPage,
  AdminUserSort,
} from '../lib/adminTypes'
import './AdminPanel.css'
import { AdminPolicyPanel } from './AdminPolicyPanel'
import { AdminPromotionPanel } from './AdminPromotionPanel'

type Tab = 'users' | 'songs' | 'reports' | 'policies' | 'promotions' | 'audits'

type PlanDraft = {
  planCode: Exclude<AdminPlanCode, 'UNASSIGNED'>
  unlimitedActiveShareLimit: string
}

type ReportDraft = {
  status: AdminReport['status']
  operatorNote: string
  reporterNotified: boolean
  uploaderNotified: boolean
  resumeShare: boolean
}

type ControlDialog = {
  song: AdminSong
  action: AdminSongControlAction
  title: string
  reason: string
  loading: boolean
  error: string
}

type EvidenceDialog = {
  songId: number
  title: string
  caseId: string
  reason: string
  loading: boolean
  error: string
  evidence: AdminEvidenceView | null
}

const PLAN_CODES: Array<Exclude<AdminPlanCode, 'UNASSIGNED'>> = [
  'OPEN_BETA', 'FREE', 'CHAPEL', 'CONCERT', 'PROFESSIONAL', 'UNLIMITED',
]

const REPORT_STATUSES: AdminReport['status'][] = [
  'RECEIVED', 'REVIEWING', 'SHARE_STOPPED', 'RESOLVED', 'REJECTED',
]

function friendlyError(error: unknown): string {
  if (error instanceof ApiError && error.status === 401) return 'mioSegno에 로그인한 뒤 다시 접속해 주세요.'
  if (error instanceof ApiError && error.status === 403) return '이 관리자 화면을 사용할 권한이 없습니다.'
  return '관리자 데이터를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'
}

function evidenceError(error: unknown): string {
  if (error instanceof ApiError && error.code === 'SEGNO_ADMIN_EVIDENCE_NOT_AVAILABLE') {
    return '이 곡에서 확인할 수 있는 업로드 증적이 없습니다.'
  }
  if (error instanceof ApiError && error.code === 'EVIDENCE_NOT_FOUND') {
    return '업로드 증적을 찾을 수 없습니다.'
  }
  if (error instanceof ApiError && error.status === 403) {
    return '증적을 열람할 권한이 없습니다.'
  }
  return '업로드 증적을 확인하지 못했습니다. 잠시 후 다시 시도해 주세요.'
}

function evidenceCaseSuffix(): string {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}${month}${day}`
}

function fmt(value?: string | null): string {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('ko-KR')
}

function userLabel(user: Pick<AdminUser, 'username' | 'displayName' | 'centralUserId'>): string {
  return user.displayName || user.username || user.centralUserId
}

function formatDuration(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds || 0))
  const hours = Math.floor(safe / 3600)
  const minutes = Math.floor((safe % 3600) / 60)
  if (hours > 0) return `${hours}시간 ${minutes}분`
  if (minutes > 0) return `${minutes}분`
  return safe > 0 ? `${safe}초` : '0분'
}

export function AdminPanel() {
  const [me, setMe] = useState<AdminMe | null>(null)
  const [dashboard, setDashboard] = useState<AdminDashboard | null>(null)
  const [users, setUsers] = useState<AdminUserPage | null>(null)
  const [songs, setSongs] = useState<AdminSongPage | null>(null)
  const [reports, setReports] = useState<AdminReport[]>([])
  const [audits, setAudits] = useState<AdminOperationAuditPage | null>(null)
  const [auditPage, setAuditPage] = useState(0)
  const [tab, setTab] = useState<Tab>('users')
  const [userPage, setUserPage] = useState(0)
  const [userPageSize, setUserPageSize] = useState<30 | 50 | 100>(30)
  const [userSearchInput, setUserSearchInput] = useState('')
  const [userQuery, setUserQuery] = useState('')
  const [userSort, setUserSort] = useState<AdminUserSort>('registered_desc')
  const [songPage, setSongPage] = useState(0)
  const [songPageSize, setSongPageSize] = useState<30 | 50 | 100>(30)
  const [songSearchInput, setSongSearchInput] = useState('')
  const [songQuery, setSongQuery] = useState('')
  const [songSourceType, setSongSourceType] = useState<AdminSongSourceFilter>('ALL')
  const [songStatus, setSongStatus] = useState<AdminSongStatusFilter>('ALL')
  const [songReport, setSongReport] = useState<AdminSongReportFilter>('ALL')
  const [songSort, setSongSort] = useState<AdminSongSort>('created_desc')
  const [includeExpiredSongs, setIncludeExpiredSongs] = useState(false)
  const [includeInactiveShares, setIncludeInactiveShares] = useState(true)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [planDrafts, setPlanDrafts] = useState<Record<string, PlanDraft>>({})
  const [reportDrafts, setReportDrafts] = useState<Record<number, ReportDraft>>({})
  const [evidenceDialog, setEvidenceDialog] = useState<EvidenceDialog | null>(null)
  const [controlDialog, setControlDialog] = useState<ControlDialog | null>(null)

  useEffect(() => {
    void bootstrap()
  }, [])

  useEffect(() => {
    if (me) void loadUsers(userPage)
  }, [userPage, userPageSize, userQuery, userSort, me])

  useEffect(() => {
    if (me) void loadSongs(songPage)
  }, [
    songPage,
    songPageSize,
    songQuery,
    songSourceType,
    songStatus,
    songReport,
    songSort,
    me,
    includeExpiredSongs,
    includeInactiveShares,
  ])

  useEffect(() => {
    if (me && tab === 'audits') void loadAudits(auditPage)
  }, [me, tab, auditPage])

  const stats = useMemo(() => dashboard ? [
    ['등록 사용자', dashboard.userCount],
    ['전체 곡', dashboard.songCount],
    ['파일 음원', dashboard.fileSongCount],
    ['YouTube', dashboard.youtubeSongCount],
    ['Active 곡 공유', dashboard.activeSongShareCount],
    ['앨범', dashboard.albumCount],
    ['Active 앨범 공유', dashboard.activeAlbumShareCount],
    ['저작권 신고', dashboard.copyrightReportCount],
    ['미처리 신고', dashboard.openCopyrightReportCount],
    ['운영 공유 중지', dashboard.adminHeldShareCount],
    ['블라인드', dashboard.blindedSongCount],
    ['현재 연습 세션', dashboard.currentPracticeSessionCount],
    ['오늘 연습 시작', dashboard.todayPracticeSessionCount],
    ['최근 7일 연습', formatDuration(dashboard.sevenDayPracticeSeconds)],
  ] : [], [dashboard])

  async function enrichSongDownloadUsage(page: AdminSongPage): Promise<AdminSongPage> {
    const fileIds = page.items.filter((song) => song.sourceType === 'FILE').map((song) => song.id)
    if (fileIds.length === 0) return page
    const usage = await api.adminShareDownloadUsage(fileIds)
    const byId = new Map(usage.map((item) => [item.songId, item]))
    return {
      ...page,
      items: page.items.map((song) => {
        const item = byId.get(song.id)
        return item ? {
          ...song,
          shareDownloadCount: item.used,
          shareDownloadLimit: item.limit,
          shareDownloadsRemaining: item.remaining,
        } : song
      }),
    }
  }

  async function bootstrap() {
    setLoading(true)
    setError('')
    try {
      const identity = await api.adminMe()
      setMe(identity)
      const [nextDashboard, nextUsers, nextSongs, nextReports] = await Promise.all([
        api.adminDashboard(),
        api.adminUsers({ page: 0, size: 30, query: '', sort: 'registered_desc' }),
        api.adminSongs({
          page: 0,
          size: 30,
          query: '',
          includeExpired: false,
          includeInactiveShares: true,
          sourceType: 'ALL',
          status: 'ALL',
          report: 'ALL',
          sort: 'created_desc',
        }),
        api.adminReports(),
      ])
      setDashboard(nextDashboard)
      setUsers(nextUsers)
      setSongs(await enrichSongDownloadUsage(nextSongs))
      setReports(nextReports)
      seedPlanDrafts(nextUsers.items)
      seedReportDrafts(nextReports)
    } catch (nextError) {
      setError(friendlyError(nextError))
      setMe(null)
    } finally {
      setLoading(false)
    }
  }

  function seedPlanDrafts(items: AdminUser[]) {
    setPlanDrafts((current) => {
      const next = { ...current }
      for (const user of items) {
        const code = user.planCode === 'UNASSIGNED' ? 'FREE' : user.planCode
        if (!next[user.centralUserId]) {
          next[user.centralUserId] = {
            planCode: code,
            unlimitedActiveShareLimit: user.unlimitedActiveShareLimit?.toString() ?? '',
          }
        }
      }
      return next
    })
  }

  function seedReportDrafts(items: AdminReport[]) {
    setReportDrafts((current) => {
      const next = { ...current }
      for (const report of items) {
        if (!next[report.id]) {
          next[report.id] = {
            status: report.status,
            operatorNote: report.operatorNote ?? '',
            reporterNotified: Boolean(report.reporterNotifiedAt),
            uploaderNotified: Boolean(report.uploaderNotifiedAt),
            resumeShare: false,
          }
        }
      }
      return next
    })
  }

  async function loadUsers(page: number) {
    try {
      const next = await api.adminUsers({
        page,
        size: userPageSize,
        query: userQuery,
        sort: userSort,
      })
      setUsers(next)
      seedPlanDrafts(next.items)
    } catch (nextError) {
      setError(friendlyError(nextError))
    }
  }

  async function loadSongs(page: number) {
    try {
      const pageResult = await api.adminSongs({
        page,
        size: songPageSize,
        query: songQuery,
        includeExpired: includeExpiredSongs,
        includeInactiveShares,
        sourceType: songSourceType,
        status: songStatus,
        report: songReport,
        sort: songSort,
      })
      setSongs(await enrichSongDownloadUsage(pageResult))
    } catch (nextError) {
      setError(friendlyError(nextError))
    }
  }

  function applyUserSearch() {
    const next = userSearchInput.trim()
    setUserPage(0)
    if (next === userQuery && userPage === 0) void loadUsers(0)
    else setUserQuery(next)
  }

  function resetUserSearch() {
    setUserSearchInput('')
    setUserQuery('')
    setUserSort('registered_desc')
    setUserPageSize(30)
    setUserPage(0)
  }

  function applySongSearch() {
    const next = songSearchInput.trim()
    setSongPage(0)
    if (next === songQuery && songPage === 0) void loadSongs(0)
    else setSongQuery(next)
  }

  function resetSongFilters() {
    setSongSearchInput('')
    setSongQuery('')
    setSongSourceType('ALL')
    setSongStatus('ALL')
    setSongReport('ALL')
    setSongSort('created_desc')
    setSongPageSize(30)
    setIncludeExpiredSongs(false)
    setIncludeInactiveShares(true)
    setSongPage(0)
  }

  async function refreshReports() {
    try {
      const next = await api.adminReports()
      setReports(next)
      seedReportDrafts(next)
    } catch (nextError) {
      setError(friendlyError(nextError))
    }
  }

  async function loadAudits(page: number) {
    try {
      setAudits(await api.adminAudits(page, 50))
    } catch (nextError) {
      setError(friendlyError(nextError))
    }
  }

  function replaceSong(updated: AdminSong) {
    setSongs((current) => current ? {
      ...current,
      items: current.items.map((item) => item.id === updated.id ? {
        ...updated,
        shareDownloadCount: item.shareDownloadCount,
        shareDownloadLimit: item.shareDownloadLimit,
        shareDownloadsRemaining: item.shareDownloadsRemaining,
      } : item),
    } : current)
  }

  function openControl(song: AdminSong, action: AdminSongControlAction) {
    const title = action === 'SHARE_STOP'
      ? '공유 중지'
      : action === 'SHARE_RESTORE'
        ? '공유 복구'
        : action === 'BLIND'
          ? '블라인드'
          : '블라인드 해제'
    setControlDialog({
      song,
      action,
      title,
      reason: '',
      loading: false,
      error: '',
    })
  }

  async function executeControl() {
    if (!controlDialog) return
    const reason = controlDialog.reason.trim()
    if (!reason) {
      setControlDialog((current) => current ? { ...current, error: '운영 사유를 입력해 주세요.' } : current)
      return
    }
    setControlDialog((current) => current ? { ...current, loading: true, error: '' } : current)
    try {
      let updated: AdminSong
      if (controlDialog.action === 'SHARE_STOP') {
        updated = await api.adminStopSongShare(controlDialog.song.id, { reason })
      } else if (controlDialog.action === 'SHARE_RESTORE') {
        updated = await api.adminRestoreSongShare(controlDialog.song.id, { reason })
      } else if (controlDialog.action === 'BLIND') {
        updated = await api.adminBlindSong(controlDialog.song.id, { reason })
      } else {
        updated = await api.adminUnblindSong(controlDialog.song.id, { reason })
      }
      replaceSong(updated)
      setControlDialog(null)
      setDashboard(await api.adminDashboard())
      if (tab === 'audits') await loadAudits(auditPage)
      setError('')
    } catch (nextError) {
      const message = userMessageForError(
        nextError,
        '운영 제어 작업을 완료하지 못했습니다.',
      )
      setControlDialog((current) => current ? { ...current, loading: false, error: message } : current)
    }
  }

  async function savePlan(user: AdminUser) {
    const draft = planDrafts[user.centralUserId]
    if (!draft) return
    if (!window.confirm(`${userLabel(user)} 사용자의 요금제를 ${draft.planCode}(으)로 변경하시겠습니까?`)) return
    const limit = draft.planCode === 'UNLIMITED' ? Number(draft.unlimitedActiveShareLimit) : null
    if (draft.planCode === 'UNLIMITED' && (limit === null || !Number.isInteger(limit) || limit <= 0)) {
      setError('Unlimited 요금제는 Active 공유 한도를 1 이상의 정수로 입력해 주세요.')
      return
    }
    try {
      const updated = await api.adminUpdatePlan(user.centralUserId, {
        planCode: draft.planCode,
        unlimitedActiveShareLimit: limit,
      })
      setUsers((current) => current ? {
        ...current,
        items: current.items.map((item) => item.centralUserId === updated.centralUserId ? updated : item),
      } : current)
      setError('')
      setDashboard(await api.adminDashboard())
      if (tab === 'audits') await loadAudits(auditPage)
    } catch (nextError) {
      setError(friendlyError(nextError))
    }
  }

  async function saveReport(report: AdminReport) {
    const draft = reportDrafts[report.id]
    if (!draft) return
    if (!window.confirm(`저작권 신고 #${report.id}의 처리 상태를 저장하시겠습니까?`)) return
    try {
      const updated = await api.adminUpdateReport(report.id, draft)
      setReports((current) => current.map((item) => item.id === updated.id ? updated : item))
      setReportDrafts((current) => ({
        ...current,
        [updated.id]: {
          status: updated.status,
          operatorNote: updated.operatorNote ?? '',
          reporterNotified: Boolean(updated.reporterNotifiedAt),
          uploaderNotified: Boolean(updated.uploaderNotifiedAt),
          resumeShare: false,
        },
      }))
      setDashboard(await api.adminDashboard())
      if (tab === 'audits') await loadAudits(auditPage)
      setError('')
    } catch (nextError) {
      setError(friendlyError(nextError))
    }
  }

  function openSongEvidence(song: { id: number; title: string; evidenceAvailable: boolean }) {
    if (!song.evidenceAvailable) return
    setEvidenceDialog({
      songId: song.id,
      title: song.title,
      caseId: `ADMIN-SONG-${song.id}-${evidenceCaseSuffix()}`,
      reason: '곡 업로드 증적 확인',
      loading: false,
      error: '',
      evidence: null,
    })
  }

  function openReportEvidence(report: AdminReport) {
    if (!report.songId || !report.evidenceAvailable) return
    setEvidenceDialog({
      songId: report.songId,
      title: report.songTitle || report.shareId,
      caseId: `COPYRIGHT-${report.id}`,
      reason: `저작권 신고 #${report.id} 처리 확인`,
      loading: false,
      error: '',
      evidence: null,
    })
  }

  async function loadEvidence() {
    if (!evidenceDialog) return
    const caseId = evidenceDialog.caseId.trim()
    const reason = evidenceDialog.reason.trim()
    if (!caseId || !reason) {
      setEvidenceDialog((current) => current ? { ...current, error: '사건번호와 열람 사유를 입력해 주세요.' } : current)
      return
    }

    setEvidenceDialog((current) => current ? { ...current, loading: true, error: '', evidence: null } : current)
    try {
      const evidence = await api.adminViewSongEvidence(evidenceDialog.songId, { caseId, reason })
      setEvidenceDialog((current) => current ? { ...current, loading: false, error: '', evidence } : current)
    } catch (nextError) {
      setEvidenceDialog((current) => current ? {
        ...current,
        loading: false,
        error: evidenceError(nextError),
        evidence: null,
      } : current)
    }
  }

  if (loading) {
    return <main className="admin-page"><section className="admin-card admin-message"><h2>mioSegno 관리자</h2><p>관리자 권한을 확인하고 있습니다.</p></section></main>
  }

  if (!me) {
    return (
      <main className="admin-page">
        <section className="admin-card admin-message">
          <h2>mioSegno 관리자</h2>
          <p>{error || '관리자 화면을 사용할 수 없습니다.'}</p>
          <button className="admin-button primary" type="button" onClick={() => { location.href = '/' }}>로그인 화면 열기</button>
        </section>
      </main>
    )
  }

  return (
    <main className="admin-page">
      <div className="admin-shell">
        <header className="admin-header">
          <div>
            <h1>mioSegno 운영 백오피스</h1>
            <p>관리자 {me.displayName || me.username} ({me.username})</p>
          </div>
          <button className="admin-button" type="button" onClick={() => { location.href = '/' }}>mioSegno로 돌아가기</button>
        </header>

        {error && <div className="admin-error" role="alert">{error}</div>}

        <section className="admin-stats" aria-label="운영 요약">
          {stats.map(([label, value]) => <div className="admin-card admin-stat" key={label}><small>{label}</small><strong>{value}</strong></div>)}
        </section>

        <div className="admin-notice">
          Phase 3B: 공유 중지/복구, 블라인드/해제, 신고·요금제 운영 변경을 통합 감사로그와 함께 관리합니다. 사용자 원본 데이터는 운영 제어로 삭제하지 않습니다.
        </div>

        <nav className="admin-tabs" aria-label="관리 메뉴">
          <button className={tab === 'users' ? 'active' : ''} type="button" onClick={() => setTab('users')}>사용자</button>
          <button className={tab === 'songs' ? 'active' : ''} type="button" onClick={() => setTab('songs')}>곡·공유</button>
          <button className={tab === 'reports' ? 'active' : ''} type="button" onClick={() => { setTab('reports'); void refreshReports() }}>저작권 신고</button>
          <button className={tab === 'policies' ? 'active' : ''} type="button" onClick={() => setTab('policies')}>요금제 정책</button>
          <button className={tab === 'promotions' ? 'active' : ''} type="button" onClick={() => setTab('promotions')}>프로모션</button>
          <button className={tab === 'audits' ? 'active' : ''} type="button" onClick={() => { setTab('audits'); setAuditPage(0) }}>운영 이력</button>
        </nav>

        {tab === 'users' && users && (
          <>
            <section className="admin-list-tools admin-list-tools--light" aria-label="사용자 목록 검색과 정렬">
              <form
                className="admin-search-row"
                onSubmit={(event) => { event.preventDefault(); applyUserSearch() }}
              >
                <input
                  type="search"
                  value={userSearchInput}
                  onChange={(event) => setUserSearchInput(event.target.value)}
                  placeholder="회원 ID 검색"
                  aria-label="회원 ID 검색"
                />
                <button className="admin-button primary" type="submit">검색</button>
                <button className="admin-button" type="button" onClick={resetUserSearch}>초기화</button>
              </form>
              <div className="admin-filter-row">
                <label className="admin-field"><span>정렬</span>
                  <select
                    value={userSort}
                    onChange={(event) => { setUserSort(event.target.value as AdminUserSort); setUserPage(0) }}
                  >
                    <option value="registered_desc">최근 등록순</option>
                    <option value="registered_asc">오래된 등록순</option>
                  </select>
                </label>
                <label className="admin-field"><span>한 페이지</span>
                  <select
                    value={userPageSize}
                    onChange={(event) => { setUserPageSize(Number(event.target.value) as 30 | 50 | 100); setUserPage(0) }}
                  >
                    <option value={30}>30명</option>
                    <option value={50}>50명</option>
                    <option value={100}>100명</option>
                  </select>
                </label>
                <small className="admin-helper-text">이름과 username은 중앙회원 표시정보이며, 현재 서버 검색은 안정적인 회원 ID를 기준으로 합니다.</small>
              </div>
            </section>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead><tr><th>사용자</th><th>서비스 등록</th><th>파일</th><th>YouTube</th><th>세뇨</th><th>Active 공유</th><th>요금제</th></tr></thead>
                <tbody>
                  {users.items.map((user) => {
                    const draft = planDrafts[user.centralUserId]
                    return (
                      <tr key={user.centralUserId}>
                        <td className="admin-user-name"><strong>{user.displayName || user.username || '표시정보 없음'}</strong><small>{user.username || user.centralUserId}</small></td>
                        <td>{fmt(user.serviceRegisteredAt)}</td>
                        <td>{user.fileSongCount}</td><td>{user.youtubeSongCount}</td><td>{user.segnoCount}</td><td>{user.activeShareCount}</td>
                        <td>
                          {draft && <div className="admin-plan-editor">
                            <select value={draft.planCode} onChange={(event) => setPlanDrafts((current) => ({ ...current, [user.centralUserId]: { ...draft, planCode: event.target.value as PlanDraft['planCode'] } }))}>
                              {PLAN_CODES.map((code) => <option key={code} value={code}>{code}</option>)}
                            </select>
                            {draft.planCode === 'UNLIMITED' && <input aria-label="Unlimited Active 공유 한도" inputMode="numeric" value={draft.unlimitedActiveShareLimit} onChange={(event) => setPlanDrafts((current) => ({ ...current, [user.centralUserId]: { ...draft, unlimitedActiveShareLimit: event.target.value } }))} />}
                            <button className="admin-button" type="button" onClick={() => void savePlan(user)}>저장</button>
                          </div>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className="admin-pager">
              <button className="admin-button" disabled={users.page <= 0} type="button" onClick={() => setUserPage(0)}>처음</button>
              <button className="admin-button" disabled={users.page <= 0} type="button" onClick={() => setUserPage(Math.max(0, users.page - 1))}>이전</button>
              <span>{users.page + 1} / {Math.max(1, users.totalPages)} · 검색 결과 {users.totalElements}명</span>
              <button className="admin-button" disabled={users.page + 1 >= users.totalPages} type="button" onClick={() => setUserPage(users.page + 1)}>다음</button>
              <button className="admin-button" disabled={users.page + 1 >= users.totalPages} type="button" onClick={() => setUserPage(Math.max(0, users.totalPages - 1))}>마지막</button>
            </div>
          </>
        )}

        {tab === 'songs' && songs && (
          <>
            <section className="admin-list-tools admin-list-tools--light" aria-label="곡 목록 검색과 필터">
              <form
                className="admin-search-row"
                onSubmit={(event) => { event.preventDefault(); applySongSearch() }}
              >
                <input
                  type="search"
                  value={songSearchInput}
                  onChange={(event) => setSongSearchInput(event.target.value)}
                  placeholder="곡 제목 / Share ID / 등록자 ID"
                  aria-label="곡 검색"
                />
                <button className="admin-button primary" type="submit">검색</button>
                <button className="admin-button" type="button" onClick={resetSongFilters}>필터 초기화</button>
              </form>

              <div className="admin-filter-row admin-filter-grid">
                <label className="admin-field"><span>종류</span>
                  <select
                    value={songSourceType}
                    onChange={(event) => { setSongSourceType(event.target.value as AdminSongSourceFilter); setSongPage(0) }}
                  >
                    <option value="ALL">전체</option>
                    <option value="FILE">파일 음원</option>
                    <option value="YOUTUBE">YouTube</option>
                  </select>
                </label>

                <label className="admin-field"><span>곡 상태</span>
                  <select
                    value={songStatus}
                    onChange={(event) => { setSongStatus(event.target.value as AdminSongStatusFilter); setSongPage(0) }}
                  >
                    <option value="ALL">전체</option>
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="REPORTED_DISABLED">신고 비활성</option>
                    <option value="EXPIRED_PENDING_DELETE">만료 처리 대기</option>
                  </select>
                </label>

                <label className="admin-field"><span>신고</span>
                  <select
                    value={songReport}
                    onChange={(event) => { setSongReport(event.target.value as AdminSongReportFilter); setSongPage(0) }}
                  >
                    <option value="ALL">전체</option>
                    <option value="OPEN">검토 필요만</option>
                    <option value="NONE">미처리 신고 없음</option>
                  </select>
                </label>

                <label className="admin-field"><span>정렬</span>
                  <select
                    value={songSort}
                    onChange={(event) => { setSongSort(event.target.value as AdminSongSort); setSongPage(0) }}
                  >
                    <option value="created_desc">최근 등록순</option>
                    <option value="created_asc">오래된 등록순</option>
                    <option value="title_asc">제목 가나다순</option>
                    <option value="title_desc">제목 역순</option>
                    <option value="expires_asc">보관 만료 임박순</option>
                    <option value="expires_desc">보관 만료 늦은순</option>
                    <option value="share_expires_asc">공유 만료 임박순</option>
                  </select>
                </label>

                <label className="admin-field"><span>한 페이지</span>
                  <select
                    value={songPageSize}
                    onChange={(event) => { setSongPageSize(Number(event.target.value) as 30 | 50 | 100); setSongPage(0) }}
                  >
                    <option value={30}>30곡</option>
                    <option value={50}>50곡</option>
                    <option value={100}>100곡</option>
                  </select>
                </label>
              </div>

              <div className="admin-song-filters" role="group" aria-label="곡 공유·만료 필터">
                <label className="admin-inline-check">
                  <input
                    type="checkbox"
                    checked={includeExpiredSongs}
                    onChange={(event) => { setIncludeExpiredSongs(event.target.checked); setSongPage(0) }}
                  />
                  만료된 곡 포함
                </label>
                <label className="admin-inline-check">
                  <input
                    type="checkbox"
                    checked={includeInactiveShares}
                    onChange={(event) => { setIncludeInactiveShares(event.target.checked); setSongPage(0) }}
                  />
                  비활성 공유 포함
                </label>
                <small className="admin-helper-text">기본값은 만료 곡 제외이며, Active 공유는 항상 포함됩니다. ‘비활성 공유 포함’을 해제하면 현재 Active 공유곡만 표시합니다.</small>
              </div>
            </section>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead><tr><th>곡</th><th>등록자</th><th>요금제</th><th>종류</th><th>세뇨</th><th>공유</th><th>다운로드</th><th>운영 제어</th><th>연습 현황</th><th>신고</th><th>업로드 증적</th><th>등록일</th><th>보관 만료</th></tr></thead>
                <tbody>{songs.items.map((song) => <tr key={song.id}>
                  <td><strong>{song.title}</strong>{song.expired && <><br /><span className="admin-badge warn">만료</span></>}<br /><small className="admin-muted">{song.shareId}</small></td>
                  <td>{song.ownerDisplayName || song.ownerUsername || song.ownerCentralUserId}</td>
                  <td><span className="admin-badge">{song.ownerPlanCode}</span></td>
                  <td>{song.sourceType === 'YOUTUBE' ? 'YouTube' : '파일 음원'}</td>
                  <td>{song.segnoCount}</td>
                  <td>
                    {song.moderationBlind
                      ? <span className="admin-badge warn">블라인드</span>
                      : song.shareActive
                        ? <span className="admin-badge ok">Active</span>
                        : <span className="admin-badge">중지</span>}
                    {song.adminShareHeld && <><br /><span className="admin-badge warn">운영 중지</span></>}
                    {song.shareDisabledReason && <><br /><small className="admin-muted">{song.shareDisabledReason}</small></>}
                  </td>
                  <td className="admin-download-metrics">
                    {song.sourceType === 'FILE' && song.shareDownloadLimit != null
                      ? <>
                          <strong>{song.shareDownloadCount ?? 0} / {song.shareDownloadLimit}</strong>
                          <small>잔여 {song.shareDownloadsRemaining ?? Math.max(0, song.shareDownloadLimit - (song.shareDownloadCount ?? 0))}회</small>
                        </>
                      : <span className="admin-muted">-</span>}
                  </td>
                  <td>
                    <div className="admin-control-stack">
                      {song.adminShareHeld
                        ? <button className="admin-button compact" type="button" onClick={() => openControl(song, 'SHARE_RESTORE')}>공유 복구</button>
                        : <button className="admin-button compact" type="button" onClick={() => openControl(song, 'SHARE_STOP')}>공유 중지</button>}
                      {song.moderationBlind
                        ? <button className="admin-button compact" type="button" onClick={() => openControl(song, 'UNBLIND')}>블라인드 해제</button>
                        : <button className="admin-button compact danger" type="button" onClick={() => openControl(song, 'BLIND')}>블라인드</button>}
                    </div>
                  </td>
                  <td className="admin-practice-metrics">
                    <strong>현재 {song.currentPracticeSessions}</strong>
                    <small>오늘 {song.todayPracticeSessions}회 · 7일 {song.sevenDayPracticeSessions}회</small>
                    <small>7일 {formatDuration(song.sevenDayPracticeSeconds)}</small>
                    <small>누적 {song.totalPracticeSessions}회 · {formatDuration(song.totalPracticeSeconds)}</small>
                  </td>
                  <td>{song.openCopyrightReport ? <span className="admin-badge warn">검토 필요</span> : '-'}</td>
                  <td>{song.evidenceAvailable
                    ? <button className="admin-button compact" type="button" onClick={() => openSongEvidence(song)}>증적 보기</button>
                    : <span className="admin-muted">-</span>}</td>
                  <td>{fmt(song.createdAt)}</td><td>{fmt(song.expiresAt)}</td>
                </tr>)}</tbody>
              </table>
            </div>
            <div className="admin-pager">
              <button className="admin-button" disabled={songs.page <= 0} type="button" onClick={() => setSongPage(0)}>처음</button>
              <button className="admin-button" disabled={songs.page <= 0} type="button" onClick={() => setSongPage(Math.max(0, songs.page - 1))}>이전</button>
              <span>{songs.page + 1} / {Math.max(1, songs.totalPages)} · 검색 결과 {songs.totalElements}곡</span>
              <button className="admin-button" disabled={songs.page + 1 >= songs.totalPages} type="button" onClick={() => setSongPage(songs.page + 1)}>다음</button>
              <button className="admin-button" disabled={songs.page + 1 >= songs.totalPages} type="button" onClick={() => setSongPage(Math.max(0, songs.totalPages - 1))}>마지막</button>
            </div>
          </>
        )}

        {tab === 'policies' && <AdminPolicyPanel />}
        {tab === 'promotions' && <AdminPromotionPanel />}

        {tab === 'reports' && (
          <div className="admin-report-list">
            {reports.length === 0 && <section className="admin-card admin-message"><p>접수된 저작권 신고가 없습니다.</p></section>}
            {reports.map((report) => {
              const draft = reportDrafts[report.id]
              return <article className="admin-report-card" key={report.id}>
                <div className="admin-report-head"><div><h3>신고 #{report.id} · {report.songTitle || report.shareId}</h3><div className="admin-report-meta">접수 {fmt(report.createdAt)} · 등록자 {report.ownerDisplayName || report.ownerUsername || report.ownerCentralUserId || '-'}</div></div><span className={report.status === 'RESOLVED' || report.status === 'REJECTED' ? 'admin-badge ok' : 'admin-badge warn'}>{report.status}</span></div>
                <div className="admin-report-grid">
                  <section><h4>신고자</h4><p>{report.reporterName} · {report.reporterEmail}</p></section>
                  <section><h4>공유 상태</h4><p>{
                    report.moderationBlind
                      ? '블라인드'
                      : report.adminShareHeld
                        ? '운영 중지'
                        : report.shareActive
                          ? 'Active'
                          : `중지${report.shareDisabledReason ? ` (${report.shareDisabledReason})` : ''}`
                  }</p></section>
                  <section><h4>권리 설명</h4><p>{report.rightsDescription}</p></section>
                  <section><h4>침해 주장</h4><p>{report.infringementDescription}</p></section>
                  <section><h4>증빙</h4><p>{report.claimantEvidence}</p></section>
                  <section><h4>추가 메모</h4><p>{report.additionalNotes || '-'}</p></section>
                  <section>
                    <h4>업로드 증적</h4>
                    <p>{report.evidenceAvailable
                      ? <button className="admin-button" type="button" onClick={() => openReportEvidence(report)}>접속 정보 확인</button>
                      : <span className="admin-muted">확인 가능한 파일 업로드 증적이 없습니다.</span>}</p>
                  </section>
                </div>
                {draft && <>
                  <div className="admin-report-actions">
                    <select value={draft.status} onChange={(event) => setReportDrafts((current) => ({ ...current, [report.id]: { ...draft, status: event.target.value as AdminReport['status'] } }))}>{REPORT_STATUSES.map((status) => <option key={status}>{status}</option>)}</select>
                    <textarea placeholder="운영자 메모" value={draft.operatorNote} onChange={(event) => setReportDrafts((current) => ({ ...current, [report.id]: { ...draft, operatorNote: event.target.value } }))} />
                    <button className="admin-button primary" type="button" onClick={() => void saveReport(report)}>처리 저장</button>
                  </div>
                  <div className="admin-checks">
                    <label><input type="checkbox" checked={draft.reporterNotified} onChange={(event) => setReportDrafts((current) => ({ ...current, [report.id]: { ...draft, reporterNotified: event.target.checked } }))} /> 신고자 통지 완료</label>
                    <label><input type="checkbox" checked={draft.uploaderNotified} onChange={(event) => setReportDrafts((current) => ({ ...current, [report.id]: { ...draft, uploaderNotified: event.target.checked } }))} /> 등록자 통지 완료</label>
                    <label><input type="checkbox" checked={draft.resumeShare} disabled={draft.status !== 'RESOLVED' && draft.status !== 'REJECTED'} onChange={(event) => setReportDrafts((current) => ({ ...current, [report.id]: { ...draft, resumeShare: event.target.checked } }))} /> 저작권 hold 해제</label>
                  </div>
                </>}
              </article>
            })}
          </div>
        )}

        {tab === 'audits' && (
          <>
            <section className="admin-card admin-audit-intro">
              <h3>운영자 조작 이력</h3>
              <p>공유 중지/복구, 블라인드/해제, 신고 처리, 사용자 요금제 및 요금제 정책 변경을 서버에서 기록합니다.</p>
            </section>
            {audits && <div className="admin-table-wrap">
              <table className="admin-table">
                <thead><tr><th>시각</th><th>운영자</th><th>작업</th><th>대상</th><th>사유</th><th>변경</th></tr></thead>
                <tbody>{audits.items.map((audit) => <tr key={audit.id}>
                  <td>{fmt(audit.createdAt)}</td>
                  <td>{audit.actorUsername || audit.actorCentralUserId}</td>
                  <td><span className="admin-badge">{audit.action}</span></td>
                  <td>{audit.targetType} · {audit.targetId}</td>
                  <td>{audit.reason}</td>
                  <td>
                    <details className="admin-audit-detail">
                      <summary>변경 내용</summary>
                      <div><strong>이전</strong><pre>{audit.beforeValue || '-'}</pre></div>
                      <div><strong>이후</strong><pre>{audit.afterValue || '-'}</pre></div>
                    </details>
                  </td>
                </tr>)}</tbody>
              </table>
            </div>}
            {audits && <div className="admin-pager">
              <button className="admin-button" disabled={audits.page <= 0} type="button" onClick={() => setAuditPage(Math.max(0, audits.page - 1))}>이전</button>
              <span>{audits.page + 1} / {Math.max(1, audits.totalPages)} · {audits.totalElements}건</span>
              <button className="admin-button" disabled={audits.page + 1 >= audits.totalPages} type="button" onClick={() => setAuditPage(audits.page + 1)}>다음</button>
            </div>}
          </>
        )}

        {controlDialog && (
          <div className="admin-evidence-backdrop" role="presentation" onMouseDown={(event) => {
            if (event.currentTarget === event.target && !controlDialog.loading) setControlDialog(null)
          }}>
            <section className="admin-evidence-dialog admin-control-dialog" role="dialog" aria-modal="true" aria-labelledby="admin-control-title">
              <div className="admin-evidence-head">
                <div>
                  <h3 id="admin-control-title">{controlDialog.title} · {controlDialog.song.title}</h3>
                  <p>사용자 데이터는 삭제하지 않습니다. 운영 사유와 변경 전·후 상태가 감사로그에 기록됩니다.</p>
                </div>
                <button className="admin-button" type="button" disabled={controlDialog.loading} onClick={() => setControlDialog(null)}>닫기</button>
              </div>
              <label className="admin-field">
                <span>운영 사유</span>
                <textarea
                  value={controlDialog.reason}
                  maxLength={1000}
                  disabled={controlDialog.loading}
                  placeholder="운영 사유를 입력해 주세요."
                  onChange={(event) => setControlDialog((current) => current ? { ...current, reason: event.target.value, error: '' } : current)}
                />
              </label>
              {controlDialog.action === 'SHARE_RESTORE' && <p className="admin-helper-text">복구 시 현재 요금제, Active 공유 한도, 권리확인, 보관·공유 만료 정책을 다시 검사합니다.</p>}
              {controlDialog.action === 'BLIND' && <p className="admin-helper-text">블라인드는 외부 공개만 차단하며 곡·세뇨·음원 메타데이터를 삭제하지 않습니다.</p>}
              {controlDialog.error && <div className="admin-error" role="alert">{controlDialog.error}</div>}
              <div className="admin-evidence-actions">
                <button className="admin-button primary" type="button" disabled={controlDialog.loading} onClick={() => void executeControl()}>
                  {controlDialog.loading ? '처리 중…' : controlDialog.title}
                </button>
              </div>
            </section>
          </div>
        )}

        {evidenceDialog && (
          <div className="admin-evidence-backdrop" role="presentation" onMouseDown={(event) => {
            if (event.currentTarget === event.target && !evidenceDialog.loading) setEvidenceDialog(null)
          }}>
            <section className="admin-evidence-dialog" role="dialog" aria-modal="true" aria-labelledby="admin-evidence-title">
              <div className="admin-evidence-head">
                <div>
                  <h3 id="admin-evidence-title">업로드 증적 · {evidenceDialog.title}</h3>
                  <p>접속 정보는 보호된 증적입니다. 단건 열람 사유가 감사로그에 기록됩니다.</p>
                </div>
                <button className="admin-button" type="button" disabled={evidenceDialog.loading} onClick={() => setEvidenceDialog(null)}>닫기</button>
              </div>

              <div className="admin-evidence-form">
                <label className="admin-field">
                  <span>사건번호</span>
                  <input
                    value={evidenceDialog.caseId}
                    maxLength={160}
                    disabled={evidenceDialog.loading}
                    onChange={(event) => setEvidenceDialog((current) => current ? { ...current, caseId: event.target.value, evidence: null } : current)}
                  />
                </label>
                <label className="admin-field">
                  <span>열람 사유</span>
                  <input
                    value={evidenceDialog.reason}
                    maxLength={500}
                    disabled={evidenceDialog.loading}
                    onChange={(event) => setEvidenceDialog((current) => current ? { ...current, reason: event.target.value, evidence: null } : current)}
                  />
                </label>
                <button className="admin-button primary" type="button" disabled={evidenceDialog.loading} onClick={() => void loadEvidence()}>
                  {evidenceDialog.loading ? '확인 중…' : '보호된 접속 정보 확인'}
                </button>
              </div>

              {evidenceDialog.error && <div className="admin-error" role="alert">{evidenceDialog.error}</div>}

              {evidenceDialog.evidence && (
                <div className="admin-evidence-result">
                  <div className="admin-evidence-grid">
                    <section><small>업로드 완료</small><strong>{fmt(evidenceDialog.evidence.putCompletedAt)}</strong></section>
                    <section><small>처리 결과</small><strong>{evidenceDialog.evidence.result || '-'}</strong></section>
                    <section><small>파일 SHA-256</small><code>{evidenceDialog.evidence.actualSha256 || evidenceDialog.evidence.expectedSha256 || '-'}</code></section>
                    <section><small>원본 음원 삭제</small><strong>{evidenceDialog.evidence.mediaDeletedAt ? `예 · ${fmt(evidenceDialog.evidence.mediaDeletedAt)}` : '아니오'}</strong></section>
                    <section><small>접속 IP</small><strong>{evidenceDialog.evidence.network?.ip || (evidenceDialog.evidence.networkState === 'ERASED' ? '보관기간 만료로 삭제됨' : '수집되지 않음')}</strong></section>
                    <section><small>수집 방식</small><strong>{evidenceDialog.evidence.network?.source === 'TRUSTED_X_REAL_IP_ONLY' ? '신뢰된 Proxy 전달 IP' : (evidenceDialog.evidence.network?.source || '-')}</strong></section>
                    <section><small>접속 포트</small><strong>{evidenceDialog.evidence.network?.port ?? '수집하지 않음'}</strong></section>
                    <section><small>Evidence ID</small><code>{evidenceDialog.evidence.evidenceId || '-'}</code></section>
                  </div>
                  <p className="admin-evidence-note">IP는 업로드 당시 인터넷 접속 회선을 식별하는 참고정보이며 GPS 위치나 실제 사용자의 물리적 위치를 직접 증명하지 않습니다.</p>
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </main>
  )
}
